export const bfgPmtilesLayerId = 'bfg-pmtiles-cells';
const bfgFeatureStateKey = 'myTeamValue';
const bfgQuantileFeatureStateKey = 'myTeamQuantile';
// Set once per hex via setFeatureState when we find which hex a company site falls into (see the
// site-badges effect in exploration-map-game.tsx). Pink to match the site badge.
export const bfgSiteFeatureStateKey = 'hasSite';
export const bfgSiteBorderColor = '#F260A7';

// 'tiers' colors hexagons by the fixed, company-size-agnostic passage buckets below
// (bfgPassageBuckets) — today's only rendering. 'heatmap' instead colors by each team's own
// visit-count quantile (see bfgQuantileFillColors) — a relative "hottest/coldest zones for us"
// read rather than an absolute one, computed server-side per tile (see getBfgTeamQuantile).

export const bfgPassageBuckets = [{
  min: 0,
  color: '#B39F93',
  labelKey: 'tier_soil'
}, {
  min: 1,
  color: '#D5EACC',
  labelKey: 'tier_sprout',
  rangeLabel: '1-4'
}, {
  min: 5,
  color: '#62BF5A',
  labelKey: 'tier_bush',
  rangeLabel: '5-12'
}, {
  min: 13,
  color: '#268B1D',
  labelKey: 'tier_tree',
  rangeLabel: '13-30'
}, {
  min: 31,
  color: '#135B0D',
  labelKey: 'tier_forest',
  rangeLabel: '+30'
}];
const bfgHoverFillColor = '#FFECA9';
const bfgHoverBorderColor = '#FCCE2C';

// Sequential, single-hue (green, matching the tiers palette above), light→dark ramp — 5 steps for
// the 5 quantile buckets the backend already bins each team's tiles into (see
// visit_count_quantile_by_team / getBfgTeamQuantile). Validated with the dataviz skill's
// palette validator (--ordinal): monotone lightness, visible step gaps, light end clears contrast.
export const bfgQuantileFillColors = ['#7fbb6f', '#61ab48', '#459730', '#297d1f', '#145c0e'];
export const bfgFillColorExpression = ['case', ['boolean', ['feature-state', 'hover'], false], bfgHoverFillColor, ['step', ['coalesce', ['feature-state', bfgFeatureStateKey], 0], bfgPassageBuckets[0].color, ...bfgPassageBuckets.slice(1).flatMap(({
  min,
  color
}) => [min, color])]];

// Every bucket except "Non exploré" is a flat 50% opacity over its own color (see
// bfgPassageBuckets); hover is also 50%, over bfgHoverFillColor instead. "Non exploré" (empty,
// value 0) hexes get 0 opacity — no fill/background at all — at every zoom, so unvisited ground
// just reads as bare map underneath rather than a solid beige tile.
export const bfgFillOpacityExpression = ['case', ['boolean', ['feature-state', 'hover'], false], 0.5, ['==', ['coalesce', ['feature-state', bfgFeatureStateKey], 0], 0], 0, 0.5];

// Heatmap mode's fill: quantile bucket (1-5, see bfgQuantileFeatureStateKey/getBfgTeamQuantile) ->
// bfgQuantileFillColors, swapped in for bfgFillColorExpression via setPaintProperty when the view
// mode toggle is on 'heatmap' (see the paint-mode effect in exploration-map-game.tsx). Static
// identity fallback (bucket n -> its own color) for use before the team's actual quantile
// boundaries have loaded — see buildBfgHeatmapFillColorExpression below for the real, tie-aware
// version used once they have.
export const bfgHeatmapFillColorExpression = ['case', ['boolean', ['feature-state', 'hover'], false], bfgHoverFillColor, ['step', ['coalesce', ['feature-state', bfgQuantileFeatureStateKey], 0], bfgQuantileFillColors[0], 2, bfgQuantileFillColors[1], 3, bfgQuantileFillColors[2], 4, bfgQuantileFillColors[3], 5, bfgQuantileFillColors[4]]];

// For each of a team's 5 quantile boundaries (upper bound of that bucket, non-decreasing — eg.
// [1, 1, 2, 3, 112]), returns which bucket (1-5) should actually color it: the lowest-numbered
// bucket among any tied with it. Ties happen on skewed distributions (many hexes sharing the same
// low passage count) — a tile the backend assigned to bucket 2 but whose boundary is tied with
// bucket 1's is recolored as bucket 1 rather than getting a needlessly darker, falsely-more-active
// shade. Also drives the merged legend rows (see buildBfgHeatmapLegendItems).
export function getBfgQuantileBucketGroups(quantiles) {
  let groupStartBucket = 1;
  return quantiles.map((value, index) => {
    if (index === 0 || value !== quantiles[index - 1]) groupStartBucket = index + 1;
    return groupStartBucket;
  });
}

// The real, per-team heatmap fill: unlike the static bfgHeatmapFillColorExpression fallback above,
// this reads the team's actual quantile boundaries (myTeam.visitCountQuantiles /
// commutingVisitCountQuantiles) so tied buckets (see getBfgQuantileBucketGroups) are recolored to
// their group's lowest bucket instead of each getting their own, falsely-distinct shade.
export function buildBfgHeatmapFillColorExpression(quantiles) {
  if (!quantiles || quantiles.length !== bfgQuantileFillColors.length) {
    return bfgHeatmapFillColorExpression;
  }
  const groups = getBfgQuantileBucketGroups(quantiles);
  const matchPairs = groups.flatMap((groupStartBucket, index) => [index + 1, bfgQuantileFillColors[groupStartBucket - 1]]);

  // MapLibre's ExpressionSpecification type expects a fixed-length tuple for 'match', which a
  // dynamically-built (5 team-specific stops) array can't satisfy structurally even though it's
  // valid at runtime — same shape as the static, hand-written bfgHeatmapFillColorExpression above.
  return ['case', ['boolean', ['feature-state', 'hover'], false], bfgHoverFillColor, ['match', ['coalesce', ['feature-state', bfgQuantileFeatureStateKey], 0], ...matchPairs, bfgQuantileFillColors[0]]];
}

// Single-bucket version of the same tie-merge, for the click popup's color swatch (which paints
// one specific hex rather than building a whole style expression).
export function getBfgHeatmapFillColor(quantiles, bucket) {
  if (!quantiles || quantiles.length !== bfgQuantileFillColors.length) {
    const clampedBucket = Math.min(Math.max(bucket, 1), bfgQuantileFillColors.length);
    return bfgQuantileFillColors[clampedBucket - 1];
  }
  const groupStartBucket = getBfgQuantileBucketGroups(quantiles)[bucket - 1] ?? bucket;
  return bfgQuantileFillColors[groupStartBucket - 1];
}

// Same tie-merging as buildBfgHeatmapFillColorExpression, for the legend: one row per group of
// tied buckets (not one per raw bucket) rather than showing duplicate ranges, colored — like the
// fill — by the group's lowest bucket.
export function buildBfgHeatmapLegendItems(quantiles) {
  // One entry per group of tied buckets: its boundary value and the color of its lowest (first
  // occurrence) bucket.
  const groups = [];
  quantiles.forEach((max, index) => {
    // Tied with the previous bucket's boundary — already represented by that (lower, lighter) row.
    if (index > 0 && max === quantiles[index - 1]) return;
    groups.push({
      color: bfgQuantileFillColors[index],
      max
    });
  });
  return groups.map(({
    color,
    max
  }, index) => {
    const min = (index === 0 ? 0 : groups[index - 1].max) + 1;
    // Last (post-merge) bucket is open-ended (like bfgPassageBuckets' own '+30') rather than
    // capped at its own boundary value.
    const isLast = index === groups.length - 1;
    return {
      color,
      rangeLabel: isLast ? `+${min}` : min >= max ? `${max}` : `${min}-${max}`
    };
  });
}

// Solid-er than the tiers fill (0.75 vs 0.5): a heatmap reads as intensity, so it leans on being
// visually denser rather than letting the basemap show through as much. Still 0 for unvisited.
export const bfgHeatmapFillOpacityExpression = ['case', ['boolean', ['feature-state', 'hover'], false], 0.5, ['==', ['coalesce', ['feature-state', bfgQuantileFeatureStateKey], 0], 0], 0, 0.75];

// Unlike the fill (always transparent when empty — see bfgFillOpacityExpression), the border still
// only shows for empty hexes above zoom 11, so the grid outline itself stays hidden across a whole
// unexplored region at low zoom (this is what actually keeps the low-zoom render cheap) while
// still tracing the hex grid over bare ground once zoomed in.
export const bfgBorderOpacityExpression = ['step', ['zoom'], ['case', ['==', ['coalesce', ['feature-state', bfgFeatureStateKey], 0], 0], 0, 1], 11.001, 1];

// Every bucket shares the same solid border color, regardless of fill.
const bfgDefaultBorderColor = '#1B6515';
export const bfgBorderColorExpression = ['case', ['boolean', ['feature-state', 'hover'], false], bfgHoverBorderColor, bfgDefaultBorderColor];
export const bfgBorderWidthExpression = ['interpolate', ['linear'], ['zoom'], 11, 0.5, 18, 1.5];

// Growth icons: soil / seed / tree / forest illustrations, trimmed to their content's bounding box
// and re-centered on a padded square canvas (so icon-anchor: 'center' lines up with the drawing,
// not the original file's off-center canvas). The actual raster files live in assets/images/bfg,
// loaded via map.loadImage() (SVG isn't decodable there — see git history for why these are PNGs,
// not SVGs).
export const bfgIconSoilId = 'bfg-icon-soil';
export const bfgIconSproutId = 'bfg-icon-sprout';
export const bfgIconBushId = 'bfg-icon-bush';
export const bfgIconTreeId = 'bfg-icon-tree';
export const bfgIconForestId = 'bfg-icon-forest';
// Same order as bfgPassageBuckets — index i here is bucket i's tier.
export const bfgTierOrder = ['soil', 'sprout', 'bush', 'tree', 'forest'];

// The bucket label *is* the tier's display name — same translated text as the legend, no separate
// growth-stage naming.
export function getBfgTierLabel(tier, t) {
  return t(`commons.challenges.exploration_map.map.${bfgPassageBuckets[bfgTierOrder.indexOf(tier)].labelKey}`);
}

// One icon layer, driven by a `tier` property — not feature-state. Icons are drawn from a
// GeoJSON point source we build ourselves (one point per hex, exactly at its center — see
// exploration-map-game.tsx), the same way geovelo's Pedal Bloom draws its icons from its own
// GeoJSON source instead of the raw pmtiles polygons. That sidesteps two problems: MapLibre bans
// `feature-state` in layout properties (so a single data-driven layer wasn't possible off the
// vector tiles directly), and a hex spanning a vector-tile boundary can otherwise get one point
// per tile it touches, duplicating its icon.
export function getBfgIconTier(properties, mode, teamId) {
  const value = getBfgTeamValue(properties, mode, teamId);
  if (value >= bfgPassageBuckets[4].min) return 'forest';
  if (value >= bfgPassageBuckets[3].min) return 'tree';
  if (value >= bfgPassageBuckets[2].min) return 'bush';
  if (value >= bfgPassageBuckets[1].min) return 'sprout';
  return 'soil';
}
export const bfgIconImageExpression = ['match', ['get', 'tier'], 'soil', bfgIconSoilId, 'sprout', bfgIconSproutId, 'bush', bfgIconBushId, 'tree', bfgIconTreeId, 'forest', bfgIconForestId, bfgIconSoilId];

// Same icon-size pattern as geovelo's Pedal Bloom game (packages/geovelo/src/games/bloom/
// use-bloom-map.ts): 'interpolate'/'exponential' base 1.4 over zoom 11→13, clamped flat outside
// that range — hexagons are a fixed real-world size so their on-screen size scales with zoom, and
// the icon needs to track that rather than staying a flat screen-pixel constant. Bloom also
// multiplies in reveal-animation/pulse factors we don't have; dropped here, kept the core shape.
// The per-tier ratios (sprout ≈ 0.62×, forest ≈ 1.64×) mirror Bloom's own level 1/3/6
// sizeFactors (0.56 / 0.9 / 1.48). Values are scaled for the 300px source images (trimmed +
// centered from the custom seed/tree/forest illustrations).
const bfgIconSizeAtZoom = {
  soil: {
    at11: 0.075,
    at13: 0.195
  },
  sprout: {
    at11: 0.06,
    at13: 0.15
  },
  bush: {
    at11: 0.1,
    at13: 0.26
  },
  // Bumped ~1.39x vs the other tiers' scaling ratio to compensate: game-icon-tree.png's opaque
  // content had to be shrunk within its 300px canvas (relative to the other tiers) so its own
  // drop shadow — cast further than the others' — would fit without being cropped at the edge.
  tree: {
    at11: 0.125,
    at13: 0.335
  },
  forest: {
    at11: 0.15,
    at13: 0.405
  }
};
export const bfgIconSizeExpression = ['interpolate', ['exponential', 1.4], ['zoom'], 11, ['match', ['get', 'tier'], 'soil', bfgIconSizeAtZoom.soil.at11, 'sprout', bfgIconSizeAtZoom.sprout.at11, 'bush', bfgIconSizeAtZoom.bush.at11, 'tree', bfgIconSizeAtZoom.tree.at11, 'forest', bfgIconSizeAtZoom.forest.at11, bfgIconSizeAtZoom.soil.at11], 13, ['match', ['get', 'tier'], 'soil', bfgIconSizeAtZoom.soil.at13, 'sprout', bfgIconSizeAtZoom.sprout.at13, 'bush', bfgIconSizeAtZoom.bush.at13, 'tree', bfgIconSizeAtZoom.tree.at13, 'forest', bfgIconSizeAtZoom.forest.at13, bfgIconSizeAtZoom.soil.at13]];

/**
 * bfg_2026 tile properties only carry per-team breakdowns as stringified JSON objects
 * (score_by_team, commuting_passages_by_team) — pull out the target team's value (score, or
 * vélotaf commuting passages) as a plain number.
 */
export function getBfgTeamValue(properties, mode, teamId) {
  if (teamId === null) return 0;
  const propertyKey = mode === 'commuting' ? 'commuting_passages_by_team' : 'score_by_team';
  const raw = properties?.[propertyKey];
  if (typeof raw !== 'string') return 0;
  try {
    const parsed = JSON.parse(raw);
    return Number(parsed[teamId]) || 0;
  } catch {
    return 0;
  }
}

// Same idea as getBfgTeamValue, but for the backend-precomputed quantile bucket (1-5, or absent
// for an unvisited hex) each tile already carries per team — visit_count_quantile_by_team /
// commuting_visit_count_quantile_by_team. This is what bfgHeatmapFillColorExpression reads
// (via bfgQuantileFeatureStateKey), so heatmap mode colors relative to each team's own
// distribution instead of the fixed bfgPassageBuckets thresholds.
export function getBfgTeamQuantile(properties, mode, teamId) {
  if (teamId === null) return 0;
  const propertyKey = mode === 'commuting' ? 'commuting_visit_count_quantile_by_team' : 'visit_count_quantile_by_team';
  const raw = properties?.[propertyKey];
  if (typeof raw !== 'string') return 0;
  try {
    const parsed = JSON.parse(raw);
    return Number(parsed[teamId]) || 0;
  } catch {
    return 0;
  }
}

// Style expressions can't parse JSON properties themselves, so this feeds the feature-state that
// bfgFillColorExpression/bfgHeatmapFillColorExpression read. Both the raw value and the quantile
// are always computed together (regardless of which view mode is active) since switching modes
// only swaps which paint expression is bound to the layer (see the paint-mode effect in
// exploration-map-game.tsx), not the feature-state itself.
export function computeBfgFeatureState(properties, mode, teamId) {
  return {
    [bfgFeatureStateKey]: getBfgTeamValue(properties, mode, teamId),
    [bfgQuantileFeatureStateKey]: getBfgTeamQuantile(properties, mode, teamId)
  };
}